import { useState, useEffect, useRef, useCallback } from 'react';

const TYPING_SPEED = 80;
const TYPING_DELAY = 800;

export const useTypingAnimation = (inView, text) => {
  const [state, setState] = useState({
    displayedText: '',
    isTypingComplete: false,
    showDescription: false,
    showCursor: true,
  });

  const typeIntervalRef = useRef(null);
  const cursorTimeoutRef = useRef(null);
  const descriptionTimeoutRef = useRef(null);
  const delayTimeoutRef = useRef(null);

  const clearTypingAnimation = useCallback(() => {
    if (typeIntervalRef.current) clearInterval(typeIntervalRef.current);
    if (cursorTimeoutRef.current) clearTimeout(cursorTimeoutRef.current);
    if (descriptionTimeoutRef.current)
      clearTimeout(descriptionTimeoutRef.current);
    if (delayTimeoutRef.current) clearTimeout(delayTimeoutRef.current);

    typeIntervalRef.current = null;
    cursorTimeoutRef.current = null;
    descriptionTimeoutRef.current = null;
    delayTimeoutRef.current = null;
  }, []);

  useEffect(() => {
    if (!inView) return;

    clearTypingAnimation();

    setState({
      displayedText: '',
      isTypingComplete: false,
      showDescription: false,
      showCursor: true,
    });

    const startTyping = () => {
      let index = 0;
      typeIntervalRef.current = setInterval(() => {
        if (index < text.length) {
          setState(prev => ({
            ...prev,
            displayedText: text.substring(0, index + 1),
          }));
          index++;
        } else {
          clearInterval(typeIntervalRef.current);
          typeIntervalRef.current = null;
          setState(prev => ({ ...prev, isTypingComplete: true }));

          cursorTimeoutRef.current = setTimeout(() => {
            setState(prev => ({ ...prev, showCursor: false }));
            descriptionTimeoutRef.current = setTimeout(() => {
              setState(prev => ({ ...prev, showDescription: true }));
            }, 300);
          }, 500);
        }
      }, TYPING_SPEED);
    };

    delayTimeoutRef.current = setTimeout(startTyping, TYPING_DELAY);

    return () => clearTypingAnimation();
  }, [inView, text, clearTypingAnimation]);

  // Cursor blinking effect
  useEffect(() => {
    if (!state.isTypingComplete || !state.showCursor) return;

    const cursorInterval = setInterval(() => {
      setState(prev => ({ ...prev, showCursor: !prev.showCursor }));
    }, 500);

    return () => clearInterval(cursorInterval);
  }, [state.isTypingComplete, state.showCursor]);

  return state;
};
